/**
 * tiktokCache — the last good TikTok dataset, kept for the next mount.
 *
 * igcomCache's twin, and needed for the same reason: a screen whose every
 * mount starts EMPTY and blocks first paint on a network read makes the back
 * button feel broken. Both sources here are slow for different reasons — the
 * live one is a paged walk at TikTok's pace with a throttle waiting at the end
 * of it, the stored one is the whole order history out of SharePoint.
 *
 *   FRESH (under REVALIDATE_AFTER_MS)  paint it and read NOTHING.
 *   USABLE (under MAX_AGE_MS)          paint it at once, re-read behind it,
 *                                      settle over the top. The screen says so.
 *   OLDER, or a different key          ignored; the load runs cold.
 *
 * MEMORY ONLY, NEVER DISK. The LIVE half carries buyer names and phone numbers
 * — unmasked for whoever is on the allowlist — and those may sit in a tab's
 * memory while it is open but must not be written to a shared machine's disk.
 * The stored half has none of them by construction, but one cache holds both,
 * so it takes the stricter rule.
 *
 * SYNCING CLEARS IT: a sync is the one moment the stored answer is known to
 * have moved, and serving the pre-sync copy would show somebody their own
 * write not having happened.
 */
import type { TiktokOverview } from "./tiktokClient";

/** Younger than this and the mount reads nothing at all. */
export const REVALIDATE_AFTER_MS = 60_000;
/** Older than this and it is not worth painting — the load runs cold. */
export const MAX_AGE_MS = 10 * 60_000;

export type TiktokSource = "stored" | "live";

interface Held { key: string; data: TiktokOverview; at: number }

let held: Held | null = null;

export function tiktokCacheKey(source: TiktokSource, days: number): string {
  return `${source}|${days}`;
}

/** The last good dataset for this key, if it is worth painting. */
export function readTiktokCache(
  key: string, now = Date.now(),
): { data: TiktokOverview; age: number } | null {
  if (!held || held.key !== key) return null;
  const age = now - held.at;
  return age < MAX_AGE_MS ? { data: held.data, age } : null;
}

/** Keep a settled dataset for the next mount. */
export function writeTiktokCache(key: string, data: TiktokOverview): void {
  held = { key, data, at: Date.now() };
}

/** Forget it — after a sync, or anything that must not be answered from
 *  memory. Exported for tests as much as for callers. */
export function clearTiktokCache(): void {
  held = null;
}
